// Policy Centre — the credit team's view of the machine-readable credit policy: Live settings, drafts &
// approvals (two-person rule), version history, the shared evaluator sandbox and the change log / audit trail.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { BookOpenCheck, FileEdit, History, FlaskConical, ScrollText, Loader2, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getPolicySchema, getPolicyLive, getPolicyVersion, getPolicyVersions, setPolicyDevActor } from "@/lib/api";
import PolicyOverview from "./PolicyOverview";
import PolicyDrafts from "./PolicyDrafts";
import PolicyVersions from "./PolicyVersions";
import PolicyTestRunner from "./PolicyTestRunner";
import PolicyAudit from "./PolicyAudit";
import PolicyDeals from "./PolicyDeals";
import { errMsg } from "./policyShared";

const TABS = [
  ["overview", "Live policy", BookOpenCheck], ["drafts", "Drafts & approvals", FileEdit], ["versions", "Versions", History],
  ["tests", "Test runner", FlaskConical], ["deals", "Deals", GitCompareArrows], ["audit", "Change log & audit", ScrollText],
];

export default function PolicyCentre() {
  const { user, hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; });
  const [schema, setSchema] = useState(null);
  const [live, setLive] = useState(null);
  const [liveMeta, setLiveMeta] = useState(null);
  const [versions, setVersions] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [actor, setActor] = useState("");
  const canEdit = hasPermission("can_edit_policy");
  const canApprove = hasPermission("can_approve_policy");

  const load = useCallback(async () => {
    const l = await getPolicyLive();
    const [meta, vs] = await Promise.all([getPolicyVersion(l.id), getPolicyVersions()]);
    setLive(l); setLiveMeta(meta); setVersions(vs.versions);
  }, []);
  useEffect(() => { getPolicySchema().then(setSchema).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load, refreshKey]);
  const changed = () => setRefreshKey((k) => k + 1);
  const pendingCount = versions.filter((v) => v.state === "pending_approval" || v.state === "approved").length;

  return (
    <div className="p-6 space-y-5" data-testid="policy-centre-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}><BookOpenCheck className="w-6 h-6 text-emerald-600" /> Policy Centre</h1>
          <p className="text-sm text-slate-500 mt-1">The UK credit policy as machine-readable settings — every stop, flag, tier and threshold the underwriting copilot runs, with reasons, approvals and history.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {live && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="policy-header-live">Live {live.version_label}</Badge>}
          {pendingCount > 0 && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300" data-testid="policy-header-pending">{pendingCount} awaiting approval / publish</Badge>}
          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600" data-testid="policy-header-role">{canApprove ? "Approver" : canEdit ? "Editor" : "Read only"}</Badge>
          {user?.is_dev_bypass && (
            <Input value={actor} onChange={(e) => { setActor(e.target.value); setPolicyDevActor(e.target.value); }} placeholder="Act as (dev only, e-mail)" className="h-7 text-xs w-56" title="Preview-only: act as another person to exercise the two-person rule" data-testid="policy-dev-actor" />
          )}
        </div>
      </div>
      {!schema || !live ? <div className="flex items-center gap-2 text-sm text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading policy…</div> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
            {TABS.map(([v, l, Icon]) => <TabsTrigger key={v} value={v} className="text-xs data-[state=active]:bg-white data-[state=active]:text-emerald-700" data-testid={`policy-tab-${v}`}><Icon className="w-3.5 h-3.5 mr-1" />{l}{v === "drafts" && pendingCount > 0 ? <span className="ml-1 rounded-full bg-amber-100 text-amber-800 text-[10px] px-1.5">{pendingCount}</span> : null}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="overview" className="mt-4"><PolicyOverview live={live} liveMeta={liveMeta} schema={schema} groups={schema.groups} canApprove={canApprove} canEdit={canEdit} onChanged={changed} /></TabsContent>
          <TabsContent value="drafts" className="mt-4"><PolicyDrafts schema={schema} groups={schema.groups} canEdit={canEdit} canApprove={canApprove} onChanged={changed} initialDraftId={params.get("draft")} /></TabsContent>
          <TabsContent value="versions" className="mt-4"><PolicyVersions key={refreshKey} canApprove={canApprove} onChanged={changed} /></TabsContent>
          <TabsContent value="tests" className="mt-4"><PolicyTestRunner live={live} versions={versions} /></TabsContent>
          <TabsContent value="deals" className="mt-4"><PolicyDeals canEdit={canEdit} /></TabsContent>
          <TabsContent value="audit" className="mt-4"><PolicyAudit refreshKey={refreshKey} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
